import { useContext, useEffect } from 'react'

import { ReplayTelemetryFiltererContext } from './ReplayTelemetryFiltererContext'
import { ReplaySessionContext } from './ReplaySessionContext'

import { ReplayCodeContext } from './ReplayCodeContext'
import { ReplayTerminalContext } from './ReplayTerminalContext'
import { ReplayChatContext } from './ReplayChatContext'
import { ReplayMouseContext } from './ReplayMouseContext'

export const ReplayOrchestrator = () => {
  // Input
  const { visibleEvents } = useContext(ReplayTelemetryFiltererContext)
  const { session } = useContext(ReplaySessionContext)

  // Outputs (Destructure VALUES for safety checks)
  const { code, setCode } = useContext(ReplayCodeContext)
  const { isRunning, setIsRunning, testResults, setTestResults, setTerminalOutput } = useContext(ReplayTerminalContext)
  const { messages, setMessages, setIsAiProcessing, currentInput, setCurrentInput } = useContext(ReplayChatContext)
  const { mousePos, isClicking, setMousePos, setIsClicking } = useContext(ReplayMouseContext)

  // =========================================================
  // 1. RECONSTRUCT CODE (Only if visibleEvents changed)
  // =========================================================
  useEffect(() => {
    if (!visibleEvents) return

    console.log('MOUSE DEBUG:', {
    visibleCount: visibleEvents.length,
    mouseMovesCount: visibleEvents.filter(e => e.event_type === 'MOUSE_MOVE').length,
    firstMouseMove: visibleEvents.find(e => e.event_type === 'MOUSE_MOVE'),
    lastMouseMove: visibleEvents.slice().reverse().find(e => e.event_type === 'MOUSE_MOVE')
  })

    // Check for CODE_SNAPSHOT events first (for rrweb converted data)
    const snapshotEvents = visibleEvents.filter(e => e.event_type === 'CODE_SNAPSHOT')
    
    if (snapshotEvents.length > 0) {
      // Use the latest snapshot
      const latestSnapshot = snapshotEvents[snapshotEvents.length - 1]
      const snapshotCode = latestSnapshot.payload?.code || ""
      
      if (snapshotCode !== code) {
        setCode(snapshotCode)
      }
      return  // Skip diff-based reconstruction
    }

    // Original diff-based reconstruction for native telemetry
    let builtCode = ""
    
    const codeEvents = visibleEvents.filter(e => 
        ['CODE_TYPE', 'CODE_PASTE', 'CODE_DELETE', 'CODE_CUT', 'CODE_UNDO', 'CODE_EDIT'].includes(e.event_type)
    )

    if (codeEvents.length > 0) {
        codeEvents.forEach(event => {
            if (event.payload && event.payload.changes) {
                event.payload.changes.forEach(change => {
                    const prefix = builtCode.slice(0, change.from)
                    const suffix = builtCode.slice(change.to)
                    builtCode = prefix + (change.text || "") + suffix
                })
            }
        })
    }

    if (builtCode !== code) {
        setCode(builtCode)
    }
  }, [visibleEvents, session, setCode, code])


  // =========================================================
  // 2. RECONSTRUCT TERMINAL & TEST RESULTS
  // =========================================================
  useEffect(() => {
    if (!visibleEvents) return

    // A. Terminal Logs - show only the LATEST output (each output replaces previous)
    const termEvents = visibleEvents.filter(e => 
        ['TERMINAL_OUTPUT', 'TERMINAL_ERROR', 'TERMINAL_RUN'].includes(e.event_type)
    )

    // Get the most recent terminal event
    const lastTermEvent = termEvents[termEvents.length - 1];
    
    let formattedLogs = [];
    if (lastTermEvent) {
      if (lastTermEvent.event_type === 'TERMINAL_RUN') {
        // Show "Running..." state
        formattedLogs = [{
          type: 'stdout',
          content: '> Executing...',
          timestamp: lastTermEvent.timestamp
        }];
      } else {
        // Show the output/error
        const content = lastTermEvent.payload?.message || lastTermEvent.payload?.output || lastTermEvent.content_details || '';
        // Use output_type if available (error/success), otherwise infer from event_type
        const isError = lastTermEvent.payload?.output_type === 'error' || 
                        lastTermEvent.event_type === 'TERMINAL_ERROR';
        formattedLogs = [{
          type: isError ? 'stderr' : 'stdout',
          content: content,
          timestamp: lastTermEvent.timestamp
        }];
      }
    }
    
    setTerminalOutput(formattedLogs)
    
    // B. Running State
    const shouldBeRunning = lastTermEvent?.event_type === 'TERMINAL_RUN'
    
    if (shouldBeRunning !== isRunning) {
        setIsRunning(shouldBeRunning)
    }

    // C. Test Results - parse from terminal output if no explicit TEST_CASE_RESULT events
    const lastTestEvent = visibleEvents
        .slice()
        .reverse()
        .find(e => e.event_type === 'TEST_CASE_RESULT')

    let newResults = []
    
    if (lastTestEvent && lastTestEvent.payload?.detailed_results) {
      // Use explicit test results if available
      newResults = lastTestEvent.payload.detailed_results
    } else if (lastTermEvent) {
      // Parse test results from terminal output text
      const output = lastTermEvent.payload?.output || lastTermEvent.payload?.message || ''
      
      // Match patterns like "3/3 passed" or "Successfully Ran: 3/3 passed"
      const passMatch = output.match(/(\d+)\/(\d+)\s*passed/i)
      
      if (passMatch) {
        const passedCount = parseInt(passMatch[1], 10)
        const totalCount = parseInt(passMatch[2], 10)
        
        // Generate synthetic test results
        for (let i = 1; i <= totalCount; i++) {
          newResults.push({
            test_case_number: i,
            passed: i <= passedCount  // First N tests passed
          })
        }
      } else if (output.includes('Error') || output.includes('error') || 
                 output.includes('Traceback') || output.includes('Exception')) {
        // If there's an error, mark all tests as failed (or reset to pending)
        // We don't know how many tests there are, so leave empty to show pending
        newResults = []
      }
    }

    if (JSON.stringify(newResults) !== JSON.stringify(testResults)) {
        setTestResults(newResults)
    }

  }, [visibleEvents, setTerminalOutput, isRunning, setIsRunning, testResults, setTestResults])


  // =========================================================
  // 3. RECONSTRUCT CHAT
  // =========================================================
  useEffect(() => {
    if (!visibleEvents) return

    // Get chat events (now properly extracted by converter)
    const msgEvents = visibleEvents.filter(e => 
        ['CHAT_SEND', 'CHAT_RESPONSE'].includes(e.event_type)
    )

    if (msgEvents.length > 0) {
      const formattedMessages = msgEvents.map((e, index) => ({
          id: index + 1,
          sender_id: e.event_type === 'CHAT_RESPONSE' ? 'AI_AGENT' : 'USER',
          content: e.payload?.text || e.payload?.content || e.payload?.message || e.content_details || "...",
          timestamp: e.timestamp
      }))
      
      setMessages(prev => {
          const welcomeMsg = prev.find(m => m.id === 'welcome-msg')
          const newMessages = welcomeMsg ? [welcomeMsg, ...formattedMessages] : formattedMessages
          if (JSON.stringify(newMessages.map(m => m.content)) !== JSON.stringify(prev.map(m => m.content))) {
              return newMessages
          }
          return prev
      })
    }

    // Ghost Thinking - show after user sends a message
    const lastMsg = msgEvents[msgEvents.length - 1]
    const shouldThink = lastMsg?.event_type === 'CHAT_SEND'
    setIsAiProcessing(shouldThink)

    // Ghost Typing - show current input text from CHAT_TYPE events
    const chatTypeEvents = visibleEvents.filter(e => e.event_type === 'CHAT_TYPE')
    const lastSendTime = lastMsg ? lastMsg.timestamp : 0
    const inputEventsAfterSend = chatTypeEvents.filter(e => e.timestamp > lastSendTime)
    const lastInputEvent = inputEventsAfterSend[inputEventsAfterSend.length - 1]
    
    const nextInput = lastInputEvent ? (lastInputEvent.payload?.text || "") : ""
    
    if (nextInput !== currentInput) {
        setCurrentInput(nextInput)
    }

  }, [visibleEvents, setMessages, setIsAiProcessing, currentInput, setCurrentInput])


  // =========================================================
  // 4. RECONSTRUCT MOUSE (The most frequent update)
  // =========================================================
  useEffect(() => {
    if (!visibleEvents) return

  

    const lastMouseMove = visibleEvents
        .slice()
        .reverse()
        .find(e => e.event_type === 'MOUSE_MOVE')

    if (lastMouseMove && lastMouseMove.payload) {
        const newX = lastMouseMove.payload.x
        const newY = lastMouseMove.payload.y

        if (mousePos.x !== newX || mousePos.y !== newY) {
            setMousePos({ x: newX, y: newY })
        }
    }

    // Check for new click events - trigger brief animation
    const clickEvents = visibleEvents.filter(e => e.event_type === 'MOUSE_CLICK')
    const lastClickTime = clickEvents.length > 0 ? clickEvents[clickEvents.length - 1].timestamp : null

    // Track last processed click to avoid re-triggering
    const lastProcessedClick = window._lastProcessedClickTime || 0

    if (lastClickTime && lastClickTime !== lastProcessedClick) {
        window._lastProcessedClickTime = lastClickTime
        setIsClicking(true)
        
        // Brief click animation - auto-clear after 200ms
        setTimeout(() => setIsClicking(false), 200)
    }

}, [visibleEvents, mousePos, setMousePos, setIsClicking])

  return null
}